<script setup lang="ts">
  import { MpCard, MpDivider, MpText } from '@borg/ui';
  import { JobDetailsSource, JobGroup, Unpacked } from '@borg/types';

  const props = defineProps<{
    jobGroup: JobGroup;
    source?: JobDetailsSource;
    styled?: boolean;
  }>();

  defineSlots<{
    videoButton: (props: { job: Unpacked<JobGroup['jobs']> }) => void;
  }>();

  const { t } = useI18n();
  const jobCard = useJobCard.provide();
  jobCard.set(props.jobGroup, props.source);
  const organizationCard = useOrganizationCard.provide();
  organizationCard.set(props.jobGroup.organization);

  const formatClass = computed(() => {
    if (props.jobGroup.format === 'EXCLUSIVE-HOME' || props.jobGroup.format === 'EXCLUSIVE') {
      return 'exclusive';
    } else if (props.jobGroup.format === 'FEATURED-HOME' || props.jobGroup.format === 'FEATURED') {
      return 'featured';
    }
    return 'featured';
  });
  const formatLabelText = computed(() => {
    if (props.jobGroup.format === 'EXCLUSIVE-HOME' || props.jobGroup.format === 'EXCLUSIVE') {
      return t('JOB_CARD.FORMAT_LABEL.EXCLUSIVE');
    } else if (props.jobGroup.format === 'FEATURED-HOME' || props.jobGroup.format === 'FEATURED') {
      return t('JOB_CARD.FORMAT_LABEL.FEATURED');
    }
    return props.jobGroup.format;
  });
</script>

<template>
  <MpCard
    class="job-card"
    :class="{
      ['job-card-styled']: styled,
      [`job-card-styled--${formatClass}`]: styled,
    }"
    shadow
    :border="styled ? undefined : `light-only`"
  >
    <MpText
      v-if="styled"
      class="job-card-styled__format-label"
      semibold
      type="body2"
    >
      {{ formatLabelText }}
    </MpText>
    <OrganizationLogo />
    <template
      v-for="(job, i) in jobGroup.jobs"
      :key="job.id"
    >
      <div class="job-card__content-container">
        <JobCardContent
          :job="job"
          :organization="jobGroup.organization"
          :source="source"
        />
        <slot
          name="videoButton"
          :job="job"
        />
      </div>
      <MpDivider
        v-if="i + 1 !== jobGroup.jobs.length"
        margin="none"
      />
    </template>
    <JobCardBadges :styled="styled" />
  </MpCard>
</template>

<style scoped lang="scss">
  .job-card {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-60);
    max-width: 100%;

    & > :first-child {
      margin-bottom: var(--mp-space-30);
    }

    &__content-container {
      display: flex;
      flex-direction: column;
      gap: var(--mp-space-40);
    }
  }

  .job-card-styled {
    $self: &;
    position: relative;
    border-color: var(--mp-color-primary-dark);

    &--exclusive {
      background-color: #f0f7fe;

      @include dark-mode() {
        background-color: #2c2f3b;
      }

      #{$self}__format-label {
        color: var(--mp-color-grey-white);
        background-color: var(--mp-color-primary-dark);
      }
    }

    &--featured {
      @include dark-mode() {
        border-color: transparent;
      }

      #{$self}__format-label {
        color: var(--mp-color-grey-black);
        background-color: var(--mp-color-system-a-dark);
      }
    }

    &__format-label {
      position: absolute;
      top: 0;
      right: 0;
      padding: var(--mp-space-30) var(--mp-space-50);
      border-top-right-radius: 14px;
      border-bottom-left-radius: var(--mp-radius-40);
    }
  }
</style>
